import React from 'react';
import { BarChart3, Sparkles, BrainCircuit, ArrowUpRight } from 'lucide-react';
import { Card } from '../../../shared/components/ui/Card';

interface EfficiencyChartProps {
    showTutorial: boolean;
}

const EfficiencyChart = ({ showTutorial }: EfficiencyChartProps) => {
    // Hardcoded chart data from prototype
    const chartData = [
        { period: 'Semana 1', invested: 300, returned: 500 },
        { period: 'Semana 2', invested: 350, returned: 1200 },
        { period: 'Semana 3', invested: 320, returned: 2800 },
        { period: 'Semana 4', invested: 400, returned: 4500 },
    ];
    const maxVal = 5000;

    return (
        <Card
            className="p-0 flex flex-col justify-between w-full relative overflow-hidden ring-1 ring-slate-900/5 shadow-xl shadow-slate-200/50"
            showTutorial={showTutorial}
            tutorial="Gráfico de Eficiencia: GRIS es tu costo en créditos. VERDE es tu ganancia. Barras verdes altas significan estrategia rentable."
        >
            <div className="flex flex-col lg:flex-row h-full">

                {/* Chart Section */}
                <div className="p-8 flex-1 relative min-h-[400px]">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-violet-500/5 rounded-full blur-[100px] pointer-events-none"></div>

                    <div className="flex items-center justify-between mb-8 relative z-10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-violet-50 rounded-lg border border-violet-100">
                                <BarChart3 className="text-violet-600" size={24} />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="text-slate-900 font-bold text-lg">Desempeño y eficiencia</h3>
                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Datos de ejemplo</span>
                                </div>
                                <p className="text-slate-500 text-sm">Inversión semanal en créditos vs. ingresos generados</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex gap-4 text-xs font-bold tracking-wider uppercase bg-slate-50 p-2 rounded-lg border border-slate-200">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-sm bg-slate-300"></div>
                                <span className="text-slate-600">Costo Invertido</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-sm bg-green-400"></div>
                                <span className="text-slate-900">Retorno de Ingresos</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-end justify-between gap-6 relative z-10 h-64 mt-12 px-4">
                        {chartData.map((data, index) => {
                            const returnHeight = (data.returned / maxVal) * 100;
                            const investHeight = (data.invested / data.returned) * 100;

                            return (
                                <div key={index} className="flex flex-col items-center gap-3 w-full h-full justify-end group cursor-pointer relative">
                                    {/* Tooltip */}
                                    <div className="absolute -top-16 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-y-2 bg-slate-900 text-white shadow-xl rounded-xl px-4 py-3 text-sm z-30 pointer-events-none whitespace-nowrap">
                                        <div className="flex justify-between gap-4 text-slate-400 mb-1">
                                            <span>Invertido:</span>
                                            <span className="text-white font-mono">${data.invested}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-green-400 font-bold border-t border-slate-700 pt-1 mt-1">
                                            <span>Retorno:</span>
                                            <span className="font-mono">${data.returned}</span>
                                        </div>
                                    </div>

                                    {/* The Single Bar */}
                                    <div className="w-full relative rounded-t-lg overflow-hidden transition-all duration-300 hover:shadow-[0_0_20px_rgba(74,222,128,0.2)] hover:scale-[1.02]"
                                        style={{ height: `${returnHeight}%` }}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-t from-green-400 to-green-300"></div>
                                        <div
                                            className="absolute bottom-0 left-0 w-full bg-slate-800/80 border-t border-white/10 transition-all duration-100"
                                            style={{ height: `${investHeight}%` }}
                                        >
                                            <div className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <span className="text-[10px] text-white/70 font-mono rotate-90 sm:rotate-0">COSTO</span>
                                            </div>
                                        </div>
                                    </div>
                                    <span className="text-xs text-slate-400 font-medium font-mono uppercase tracking-wide">{data.period}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Analysis Panel */}
                <div className="w-full lg:w-80 bg-slate-50 border-t lg:border-t-0 lg:border-l border-slate-100 p-8 flex flex-col justify-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-6 opacity-20 pointer-events-none">
                        <Sparkles className="text-yellow-400" size={64} />
                    </div>

                    <div className="relative z-10 space-y-6">
                        <div>
                            <div className="flex items-center gap-2 mb-2 text-violet-500">
                                <BrainCircuit size={18} />
                                <span className="text-xs font-bold uppercase tracking-wider">Análisis Financiero IA</span>
                            </div>
                            <div className="text-5xl font-bold text-slate-900 mb-1 tracking-tight">3.4x</div>
                            <div className="text-sm text-green-600 font-medium flex items-center gap-1">
                                <ArrowUpRight size={14} /> Multiplicador Realizado
                            </div>
                        </div>

                        <div className="space-y-3">
                            <p className="text-sm text-slate-600 leading-relaxed border-l-2 border-violet-200 pl-3">
                                Tu eficiencia de inversión es alta. <span className="text-slate-900 font-medium">Tu mercado tiene 65% sin alcanzar todavía.</span>
                            </p>
                            <p className="text-sm text-slate-900 font-medium bg-white p-3 rounded-lg border border-slate-100 shadow-sm">
                                Recomendación: subí tu cupo mensual de créditos un <span className="text-violet-600 font-bold decoration-violet-200 underline">20%</span>.
                            </p>
                        </div>


                    </div>
                </div>

            </div>
        </Card>
    );
};

export default EfficiencyChart;
